/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Card,
  Flex,
  Text,
  TextInput,
  Tooltip,
  Transition
} from "@mantine/core";
import { IconPencil, IconUpload } from "@tabler/icons-react";
import {
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  updateUserAttributes,
  UpdateUserAttributesInput,
  UpdateUserAttributesOutput
} from "aws-amplify/auth";
import { getUrl, uploadData } from "aws-amplify/storage";
import { useEffect, useRef, useState } from "react";
import UserStats from "../components/Profile/UserStats.tsx";
import { useUserProvider } from "../utils/UserProvider.tsx";
import { appShowNotification } from "../utils/utils.tsx";

function Profile({ canEdit = true }) {
  const [name, setName] = useState("");
  const { amplifyUser } = useUserProvider();
  const [editName, setEditName] = useState<boolean>(false);
  //changed from null to "" will this cause issues between expo and react platforms?
  const [profileImage, setProfileImage] = useState(
    "profile-image-placeholder.jpeg"
  );
  const [email, setEmail] = useState("");

  const [refreshing, setRefreshing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();

      setName(userInfo.name!);
      if (!userInfo.name) {
        setEditName(true);
      }
      setEmail(userInfo.email!);
      const identityId = (await fetchAuthSession()).identityId;
      if (userInfo.picture) {
        // const s3Image = await Storage.get(userInfo.picture, { level: 'private' });
        const s3Image = (
          await getUrl({
            path: `private/${identityId}/${userInfo.picture}`,
            options: { validateObjectExistence: true }
          })
        ).url.toString();

        setProfileImage(s3Image);
      } else {
        // const s3Image = await Storage.get('pet-image-placeholder.png', { level: 'public' });
        const s3Image = (
          await getUrl({
            path: "public/pet-image-placeholder.png",
            options: { validateObjectExistence: true }
          })
        ).url.toString();
        setProfileImage(s3Image);
      }

      // end refreshing
      setRefreshing(false);
    } catch (err) {
      console.log(err);
    }
  }
  const handleProfileImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      //get the file extension
      const fileExtension = "." + file.name?.replace(/ /g, "-");
      //convert file to blob
      const blob = new Blob([file], { type: file.type });
      //get the storagepath
      const storagePath = `profilePicture/ProfilePic_${fileExtension}`;
      setRefreshing(true);
      const identityId = (await fetchAuthSession()).identityId;

      try {
        // Upload the Blob
        // await Storage.put(storagePath, blob, { level: "private" });
        const s3Put = uploadData({
          path: `private/${identityId}/${storagePath}`,
          data: blob,
          options: {}
        });
        await s3Put.result;

        // Get the download URL
        // const downloadUrl = await Storage.get(storagePath, { level: "private" });
        const downloadUrl = (
          await getUrl({
            path: `private/${identityId}/${storagePath}`,
            options: { validateObjectExistence: true }
          })
        ).url.toString();

        // Update the profile image
        setProfileImage(downloadUrl);

        // Update user attribute with the image URL
        // await Auth.updateUserAttributes(user, { picture: storagePath });
        const updatedAttrInput: UpdateUserAttributesInput = {
          userAttributes: { picture: storagePath }
        };

        await updateUserAttributes(updatedAttrInput);
        appShowNotification({
          title: "Profile image updated successfully!",
          type: "success"
        });
        console.log("Profile image URL updated:", downloadUrl);
      } catch (error) {
        console.error("Error uploading or updating profile image:", error);
        appShowNotification({
          title: "Failed to upload or update profile image!",
          type: "error"
        });
      }
      setRefreshing(false);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      setProfileImage("");
    }
  };

  const handleSetName = async (newName: string) => {
    setName(newName);
    setRefreshing(true);
    try {
      const updatedAttrInput: UpdateUserAttributesInput = {
        userAttributes: { name: newName }
      };
      const updatedAttrOutput: UpdateUserAttributesOutput =
        await updateUserAttributes(updatedAttrInput);
      console.log(updatedAttrOutput);
      appShowNotification({
        title: "Name updated successfully!",
        type: "success"
      });
    } catch (error) {
      console.error("Error updating user:", error);
      appShowNotification({ title: "Failed to update name!", type: "error" });
    }
    setRefreshing(false);
    setEditName(false);
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  return (
    <>
      <Card shadow="0" padding="xl" radius="">
        <Card.Section
          h={140}
          style={{
            backgroundImage:
              "linear-gradient(to right, #003554, #1a4a6b, #2d5f82, #3f7499, #0582CA)"
          }}
        />
        <Box className="box-avatar">
          <input
            type="file"
            accept="image/png,image/jpeg"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleProfileImageUpload}
          />
          <Avatar src={profileImage} size={80} radius={80} mx="auto" />
          {canEdit && (
            <Tooltip label="Upload avatar" withArrow>
              <ActionIcon
                loading={refreshing}
                radius="lg"
                color="#adb5bd"
                className="btn-change-avatar"
                size={"sm"}
                onClick={() => fileInputRef.current?.click()}
              >
                <IconUpload size={"16"} />
              </ActionIcon>
            </Tooltip>
          )}
        </Box>
        <Text ta="center" fz="sm" mt={"sm"} c="dimmed">
          {email}
        </Text>
        <Box my={"xs"} pos={"relative"}>
          <Transition
            mounted={!editName}
            transition="fade"
            duration={400}
            timingFunction="ease"
          >
            {(styles) => (
              <Flex
                style={styles}
                pos={"absolute"}
                justify={"center"}
                align={"center"}
                className="box-edit-name"
              >
                <Text ta="center" fz="lg" fw={500} mr={"5"}>
                  {name}
                </Text>
                {canEdit && (
                  <Tooltip label="Edit Name" withArrow>
                    <ActionIcon
                      radius="lg"
                      color="#adb5bd"
                      size={"sm"}
                      onClick={() => setEditName(true)}
                    >
                      <IconPencil size={"16"} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Flex>
            )}
          </Transition>
          <Transition
            mounted={editName}
            transition="fade"
            duration={400}
            timingFunction="ease"
          >
            {(styles) => (
              <Flex
                style={styles}
                w={300}
                pos={"absolute"}
                mx={"auto"}
                className="box-edit-name"
              >
                <TextInput
                  defaultValue={name}
                  radius="lg"
                  placeholder="Enter your name"
                  onChange={(event) => {
                    setName(event.currentTarget.value);
                  }}
                />
                <Button
                  onClick={async () => await handleSetName(name)}
                  radius={"lg"}
                  mx={"auto"}
                  loading={refreshing}
                >
                  Save
                </Button>
              </Flex>
            )}
          </Transition>
        </Box>

        <UserStats amplifyUser={amplifyUser} />
      </Card>
    </>
  );
}

export default Profile;
